/**
 * @file TournamentContext/types.ts
 * @description TypeScript types for TournamentContext payment-related state
 * 
 * Task 19.1: Add TypeScript types for payment state in TournamentContext
 * Reference: .taskmaster/docs/task-19-tournament-context-payment.md
 */

import { ReactNode } from 'react';
import { EntryFee, TournamentResponse } from '../../types/tournament';
import { PaymentStatus, GetPaymentStatusResponse } from '../../types/payment';
// Task 39.3: Import CryptoTradingPairListItem for typed tournamentPairList
import { CryptoTradingPairListItem } from '../../api/tournamentService';

/**
 * Payment-related state fields for TournamentContext
 */
export interface TournamentPaymentState {
  /** Entry fee configuration from tournament details */
  entryFee: EntryFee | null;
  /** Current user's payment status for this tournament */
  userPaymentStatus: PaymentStatus | null;
  /** Whether payment status check is in progress */
  isCheckingPaymentStatus: boolean;
  /** Error from payment status check, if any */
  paymentStatusError: Error | null;
}

/**
 * Props for TournamentProvider component
 */
export interface TournamentProviderProps {
  children: ReactNode;
  tournamentId: string | number;
  /** Optional prefetched tournament details (e.g., from Play button) to avoid refetch on mount. */
  initialTournamentDetails?: TournamentResponse | null;
  /**
   * Optional flag to control whether TournamentContext should fetch
   * crypto trading pairs for this tournament. Some views (e.g. wallet
   * address entry) do not need trading pairs and can disable this to
   * avoid unnecessary API calls.
   */
  loadTradingPairs?: boolean;
}

/**
 * Full context value shape for TournamentContext
 * Combines existing fields with new payment-related fields
 */
export interface TournamentContextValue {
  // Existing fields
  tournamentStatus: string | null;
  joinedStatus: boolean | null;
  tournamentDetails: TournamentResponse | null;
  isJoinedToTournamentWithId: (tournamentId: string | number) => Promise<boolean>;
  // TEAM_003: Explicit action to refresh tournament state from backend after payment/join
  refreshTournamentState: (detailsOverride?: TournamentResponse | null) => Promise<void>;
  /** Apply freshly fetched tournament details without triggering a refetch. */
  applyTournamentDetails: (details: TournamentResponse | null, userDetails?: { tournaments?: Array<{ id: string | number }> } | null) => void;
  // TEAM_007: Optional socket-driven status update hook (SSOT via tournamentStatusChanged)
  applyTournamentStatusChangedEvent?: (event: unknown) => void;
  setMyAssets: (assets: unknown) => void;
  myAssets: unknown;
  // Task 39.3: Typed with CryptoTradingPairListItem
  tournamentPairList: CryptoTradingPairListItem[];
  tournamentId: string | number;
  
  // New payment-related fields (Task 19)
  entryFee: EntryFee | null;
  userPaymentStatus: PaymentStatus | null;
  isCheckingPaymentStatus: boolean;
  paymentStatusError: Error | null;
  // Task 39.1: Second parameter is paymentId (not userId) - validates tournamentId match
  checkUserPaymentStatus: (tournamentId: string | number, paymentId: string) => Promise<void>;
  
  // Computed helpers
  isPaidTournament: boolean;
  isPaymentRequired: boolean;
  hasActivePaymentSession: boolean;
  // Optional current payment snapshot for helpers like usePaymentSession
  currentPayment?: GetPaymentStatusResponse;
}

/**
 * Type guard to check if a value is a valid EntryFee
 */
export const isEntryFee = (value: unknown): value is EntryFee => {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    (obj.type === 'FREE' || obj.type === 'PAID') &&
    typeof obj.amount === 'string' &&
    typeof obj.currency === 'string'
  );
};

/**
 * Default FREE entry fee for tournaments without explicit entry fee
 */
export const DEFAULT_FREE_ENTRY_FEE: EntryFee = {
  type: 'FREE',
  amount: '0',
  currency: 'POL',
};
